import type { LogtoErrorCode } from '@logto/phrases';
import { experience, type RequestErrorBody } from '@logto/schemas';
import { HTTPError, TimeoutError } from 'ky';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useNavigateWithPreservedSearchParams from './use-navigate-with-preserved-search-params';
import useToast from './use-toast';

export type ErrorHandlers = {
  [key in LogtoErrorCode]?: (error: RequestErrorBody) => void | Promise<void>;
} & {
  // Overwrite default global error handle logic
  global?: (error: RequestErrorBody) => void | Promise<void>;
  /** Runs after the matched handler, for resetting UI state the failed request left behind. */
  callback?: (error: RequestErrorBody) => void | Promise<void>;
};

/**
 * Built-in handlers for terminal error codes that should land on a dedicated page
 * instead of a transient toast. Specific per-call handlers still take precedence;
 * these only run when the caller did not handle the code and before falling back
 * to `global` / toast.
 */
const getTerminalErrorPath = (code: LogtoErrorCode): string | undefined => {
  if (code === 'user.suspended') {
    return `/${experience.routes.accountSuspended}`;
  }

  return undefined;
};

const useErrorHandler = () => {
  const { t } = useTranslation();
  const { setToast } = useToast();
  const navigate = useNavigateWithPreservedSearchParams();

  const handleError = useCallback(
    async (error: unknown, errorHandlers?: ErrorHandlers) => {
      if (error instanceof HTTPError) {
        try {
          // V2: error body is pre-parsed and available on error.data (response body
          // is already consumed — awaiting error.response.json() would throw).
          // eslint-disable-next-line no-restricted-syntax
          const logtoError = error.data as RequestErrorBody;

          const { code, message } = logtoError;
          const terminalPath = getTerminalErrorPath(code);

          const handler =
            errorHandlers?.[code] ??
            (terminalPath
              ? () => {
                  navigate(terminalPath, { replace: true });
                }
              : errorHandlers?.global);

          if (handler) {
            await handler(logtoError);
          } else {
            // Prefer the localized phrase for this error code (consistent with the timeout/
            // unknown branches below and with ErrorMessage); fall back to the server message
            // only when no phrase exists, so users never see a raw developer-facing string.
            setToast(t(`error.${code}`, { defaultValue: message }), { variant: 'danger' });
          }

          await errorHandlers?.callback?.(logtoError);

          return;
        } catch (error) {
          setToast(t('error.unknown'), { variant: 'danger' });
          console.error(error);

          return;
        }
      }

      if (error instanceof TimeoutError) {
        setToast(t('error.timeout'), { variant: 'danger' });

        return;
      }

      setToast(t('error.unknown'), { variant: 'danger' });
      console.error(error);
    },
    [navigate, setToast, t]
  );

  return handleError;
};

export default useErrorHandler;
